import React, {useCallback, useEffect, useMemo, useState} from "react";
import useIsMounted from "../../../../../../../hooks/use-is-mounted";
import Grid2 from "@mui/material/Unstable_Grid2";
import {Box, Card, Divider, IconButton, Skeleton, Tooltip, Typography} from "@mui/material";
import {DeleteOutline, InfoOutlined} from "@mui/icons-material";
import Form, {MuiRFF, Yup} from "../../../../../../../components/form";
import ValidateMessages from "../../../../../../../../core/models/static/texts/vallidate-messages";
import Button from "../../../../../../../components/base/button/button";
import {ReactComponent as CheckboxIcon} from "../../../../../../../../assets/images/check-box/checkbox.svg";
import {ReactComponent as ActiveCheckboxIcon} from "../../../../../../../../assets/images/check-box/checkbox-active.svg";
import {useAppDispatch} from "../../../../../../../redux";
import {setReduxDialogState} from "../../../../../../../redux/slices/dialogs/actions";
import ReduxStoreRoutes from "../../../../../../../../core/models/static/routes/redux-store-routes";
import Api from "../../../../../../../../core/services/api/api";


export const PaymentProvidersConfigurationsSection = () => {
    const [loading, setLoading] = useState(true);
    const [configs, setConfigs] = useState({
        moneris: null,
        stripe: null
    });
    const [submitting, setSubmitting] = useState([]);
    const isMounted = useIsMounted();
    const dispatch = useAppDispatch();

    /**
     * Fetches the payment provider configurations from the server (Mocked)
     */
    const getConfigurations = useCallback(async () => {
        setLoading(true);
        const response = await Api.getPOSStoreInformation();
        if (!isMounted()) return;
        setLoading(false);
        if (response?.resultFlag) {
            setConfigs({
                moneris: response.data?.moneris ?? null,
                stripe: response.data?.stripe ?? null
            });
        }
    }, [isMounted]);

    /**
     * Fetches the data on mount
     */
    useEffect(() => {
        void getConfigurations();
    }, [getConfigurations]);

    /**
     * Updates a specific payment provider token (Mocked)
     */
    const updateConfig = useCallback(async (provider, model) => {
        if (model === null) {
            const confirmed = await new Promise((resolve) => {
                dispatch(setReduxDialogState(ReduxStoreRoutes.dialogs.confirmation, {
                    open: true,
                    title: 'Remove Payment Provider Configuration',
                    description: `Are you sure you want to remove the ${provider === 'stripe' ? "Striep" : "Moneris"} payment provider configuration? This action cannot be undone.`,
                    confirmButtonText: 'Yes, Remove',
                    cancelButtonText: 'Cancel',
                    maxWidth: 'sm',
                    fullWidth: false,
                    onClose: (confirmed) => {
                        resolve(confirmed);
                        return Promise.resolve(true);
                    }
                }));
            })
            if (!confirmed)
                return;
        }
        setSubmitting(prevState => [...prevState, provider]);
        const response = await Api.savePOSStoreInformation({
            [provider]: model,
            updatedProperties: [provider[0].toUpperCase() + provider.slice(1)]
        })
        if (!isMounted()) return;
        setSubmitting(prevState => prevState.filter(e => e !== provider));

        if (response?.resultFlag) {
            setConfigs(prevState => ({
                ...prevState,
                [provider]: model,
            }));
        }
        return response;
    }, [dispatch, isMounted]);

    return (
        <>
            {
                loading &&
                <>
                    <Grid2 xs={12}>
                        <Skeleton sx={{transform: 'none'}} width={'100%'} height={120}/>
                    </Grid2>
                    <Grid2 xs={12}>
                        <Skeleton sx={{transform: 'none'}} width={'100%'} height={120}/>
                    </Grid2>
                </>
            }
            {
                !loading &&
                <>
                    <MonerisConfigurationForm
                        initialData={configs.moneris}
                        onSubmit={(model) => updateConfig('moneris', model)}
                        submitting={submitting.includes('moneris')}
                    />
                    <StripeConfigurationForm
                        initialData={configs.stripe}
                        onSubmit={(model) => updateConfig('stripe', model)}
                        submitting={submitting.includes('stripe')}
                    />
                </>
            }
        </>
    );
};

const MonerisConfigurationForm = ({initialData, onSubmit, submitting}) => {
    const initialValues = useMemo(() => ({
        storeName: initialData?.storeName ?? "",
        token: initialData?.token ?? "",
        isTestMode: initialData?.isTestMode ?? false,
    }), [initialData]);

    const schema = useMemo(() => {
        return Yup.object().shape({
            storeName: Yup.string().required(ValidateMessages.required),
            token: Yup.string().required(ValidateMessages.required),
            isTestMode: Yup.boolean().required(ValidateMessages.required),
        });
    }, []);

    const validate = useMemo(() => MuiRFF.makeValidate(schema), [schema]);
    const required = useMemo(() => MuiRFF.makeRequired(schema), [schema]);

    return (
        <Grid2 xs={12}>
            <Card variant={'elevation'} sx={{boxShadow: 'none', backgroundImage: 'none', padding: 2, width: '100%'}}>
                <Form
                    onSubmit={(values) => onSubmit(values)}
                    validate={validate}
                    initialValues={initialValues}
                    formProps={{id: 'moneris-provider-form'}}
                >
                    {({values}) => (
                        <Grid2 container spacing={2}>
                            <Grid2 xs={12} sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                                <Typography variant={'subtitle1'} fontWeight={600}>
                                    Moneris Configuration
                                </Typography>
                                {
                                    initialData !== null &&
                                    <Tooltip title={'Delete Moneris Configuration'}>
                                        <IconButton
                                            color="error"
                                            onClick={() => onSubmit(null)}
                                            disabled={submitting}
                                            size="small"
                                        >
                                            <DeleteOutline/>
                                        </IconButton>
                                    </Tooltip>
                                }
                            </Grid2>
                            <Grid2 xs={12}>
                                <Divider variant={'fullWidth'} orientation={'horizontal'}/>
                            </Grid2>

                            <Grid2 xs={12} xl={6}>
                                <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 1}}>
                                    <Typography variant="body2" fontWeight={500}>Store Name</Typography>
                                    <Tooltip title={'The Store Name provided by Moneris.'}>
                                        <InfoOutlined color={'primary'} sx={{fontSize: 18}}/>
                                    </Tooltip>
                                </Box>
                                <MuiRFF.TextField
                                    name="storeName"
                                    placeholder='e.g. store123'
                                    required={required.storeName}
                                    fullWidth
                                    disabled={submitting}
                                />
                            </Grid2>

                            <Grid2 xs={12} xl={6}>
                                <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 1}}>
                                    <Typography variant="body2" fontWeight={500}>Private Token</Typography>
                                    <Tooltip title={'The API Private Token for Moneris.'}>
                                        <InfoOutlined color={'primary'} sx={{fontSize: 18}}/>
                                    </Tooltip>
                                </Box>
                                <MuiRFF.TextField
                                    name="token"
                                    type="password"
                                    placeholder='Enter Moneris token'
                                    required={required.token}
                                    fullWidth
                                    disabled={submitting}
                                />
                            </Grid2>

                            <Grid2 xs={12}>
                                <MuiRFF.Checkboxes
                                    name={'isTestMode'}
                                    color={'primary'}
                                    icon={<CheckboxIcon/>}
                                    checkedIcon={<ActiveCheckboxIcon/>}
                                    data={{
                                        value: values.isTestMode,
                                        label: 'Test Mode',
                                    }}
                                    required={required.isTestMode}
                                    formControlLabelProps={{
                                        sx: {marginBlockEnd: 0, marginInlineEnd: 0,},
                                    }}
                                />
                            </Grid2>
                            <Grid2 xs={12} sx={{display: 'flex', justifyContent: 'flex-end', mt: 2}}>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                    disabled={submitting || (values.storeName === initialValues.storeName && values.token === initialValues.token && values.isTestMode === initialValues.isTestMode)}
                                    form="moneris-provider-form"
                                    sx={{minWidth: 200}}
                                >
                                    {
                                        initialData === null ?
                                            (submitting ? "Setting up..." : "Setup") :
                                            (submitting ? "Saving..." : "Save Changes")
                                    }
                                </Button>
                            </Grid2>
                        </Grid2>
                    )}
                </Form>
            </Card>
        </Grid2>
    );
};

const StripeConfigurationForm = ({initialData, onSubmit, submitting}) => {
    const initialValues = useMemo(() => ({
        publicToken: initialData?.publicToken ?? "",
        privateToken: initialData?.privateToken ?? "",
        isTestMode: initialData?.isTestMode ?? false,
    }), [initialData]);

    const schema = useMemo(() => {
        return Yup.object().shape({
            publicToken: Yup.string().required(ValidateMessages.required),
            privateToken: Yup.string().required(ValidateMessages.required),
            isTestMode: Yup.boolean().required(ValidateMessages.required),
        });
    }, []);

    const validate = useMemo(() => MuiRFF.makeValidate(schema), [schema]);
    const required = useMemo(() => MuiRFF.makeRequired(schema), [schema]);

    return (
        <Grid2 xs={12}>
            <Card variant={'elevation'} sx={{boxShadow: 'none', backgroundImage: 'none', padding: 2, width: '100%'}}>
                <Form
                    onSubmit={(values) => onSubmit(values)}
                    validate={validate}
                    initialValues={initialValues}
                    formProps={{id: 'stripe-provider-form'}}
                >
                    {({values}) => (
                        <Grid2 container spacing={2}>
                            <Grid2 xs={12} sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                                <Typography variant={'subtitle1'} fontWeight={600}>
                                    Stripe Configuration
                                </Typography>
                                {
                                    initialData !== null &&
                                    <Tooltip title={'Delete Stripe Configuration'}>
                                        <IconButton
                                            color="error"
                                            onClick={() => onSubmit(null)}
                                            disabled={submitting}
                                            size="small"
                                        >
                                            <DeleteOutline/>
                                        </IconButton>
                                    </Tooltip>
                                }
                            </Grid2>
                            <Grid2 xs={12}>
                                <Divider variant={'fullWidth'} orientation={'horizontal'}/>
                            </Grid2>

                            <Grid2 xs={12} xl={6}>
                                <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 1}}>
                                    <Typography variant="body2" fontWeight={500}>Public Token</Typography>
                                    <Tooltip title={'The Publishable API Key provided by Stripe.'}>
                                        <InfoOutlined color={'primary'} sx={{fontSize: 18}}/>
                                    </Tooltip>
                                </Box>
                                <MuiRFF.TextField
                                    name="publicToken"
                                    placeholder='e.g. pk_test_...'
                                    required={required.publicToken}
                                    fullWidth
                                    disabled={submitting}
                                />
                            </Grid2>

                            <Grid2 xs={12} xl={6}>
                                <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 1}}>
                                    <Typography variant="body2" fontWeight={500}>Private Token</Typography>
                                    <Tooltip title={'The Secret API Key for Stripe.'}>
                                        <InfoOutlined color={'primary'} sx={{fontSize: 18}}/>
                                    </Tooltip>
                                </Box>
                                <MuiRFF.TextField
                                    name="privateToken"
                                    type="password"
                                    placeholder='e.g. sk_test_...'
                                    required={required.privateToken}
                                    fullWidth
                                    disabled={submitting}
                                />
                            </Grid2>

                            <Grid2 xs={12}>
                                <MuiRFF.Checkboxes
                                    name={'isTestMode'}
                                    color={'primary'}
                                    icon={<CheckboxIcon/>}
                                    checkedIcon={<ActiveCheckboxIcon/>}
                                    data={{
                                        value: values.isTestMode,
                                        label: 'Test Mode',
                                    }}
                                    required={required.isTestMode}
                                    formControlLabelProps={{
                                        sx: {marginBlockEnd: 0, marginInlineEnd: 0,},
                                    }}
                                />
                            </Grid2>
                            <Grid2 xs={12} sx={{display: 'flex', justifyContent: 'flex-end', mt: 2}}>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                    disabled={submitting || (values.publicToken === initialValues.publicToken && values.privateToken === initialValues.privateToken && values.isTestMode === initialValues.isTestMode)}
                                    form="stripe-provider-form"
                                    sx={{minWidth: 200}}
                                >
                                    {
                                        initialData === null ?
                                            (submitting ? "Setting up..." : "Setup") :
                                            (submitting ? "Saving..." : "Save Changes")
                                    }
                                </Button>
                            </Grid2>
                        </Grid2>
                    )}
                </Form>
            </Card>
        </Grid2>
    );
};
